import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { FaSearch } from "react-icons/fa";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import Loader from "../../../components/Loader/Loader";
import Pagination from "../../../components/Pagination/Pagination";
import {
  getAssignableStudents,
  updateSurvey,
} from "../../../services/SurveyService";

const initials = (name) =>
  name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const SurveyAudienceTab = ({ survey, onAudienceSaved }) => {
  const [pendingAudienceType, setPendingAudienceType] = useState(
    survey.is_student_specific ? "selected" : "whole",
  );
  const [search, setSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAssigned, setTotalAssigned] = useState(0);
  const visitedPagesRef = useRef(new Set());

  const fetchStudents = useCallback(
    async (page) => {
      try {
        setIsLoading(true);
        const response = await getAssignableStudents({
          survey_id: survey.id,
          program_ids: (survey.programs || [])
            .map((p) => p.id ?? p.program_id ?? p.program?.id)
            .filter(Boolean),
          assigned: 1,
          page,
        });
        if (response.status === 200) {
          const body = response.data;
          const rows = Array.isArray(body.data) ? body.data : body.data?.data || [];
          setStudents(rows);
          setCurrentPage(body.current_page || page);
          setTotalPages(body.last_page || 1);
          setTotalAssigned(body.total ?? rows.length);
          // Every row from this endpoint is already assigned — merge each
          // page's students in once so re-visiting a page doesn't undo a
          // deselection made earlier in the session.
          if (!visitedPagesRef.current.has(page)) {
            visitedPagesRef.current.add(page);
            setSelectedStudentIds((prev) =>
              Array.from(new Set([...prev, ...rows.map((row) => row.student_id)])),
            );
          }
        }
      } catch (error) {
        console.error("API Error:", error.response);
      } finally {
        setIsLoading(false);
      }
    },
    [survey.id, survey.programs],
  );

  useEffect(() => {
    visitedPagesRef.current = new Set();
    setSelectedStudentIds([]);
    fetchStudents(1);
  }, [fetchStudents]);

  const handlePageChange = (item) => fetchStudents(item.selected + 1);

  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return students;
    return students.filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        String(s.id_number).toLowerCase().includes(term) ||
        String(s.roll_no).includes(term),
    );
  }, [search, students]);

  const groupedByClass = useMemo(() => {
    const groups = {};
    filteredStudents.forEach((s) => {
      if (!groups[s.program_name]) groups[s.program_name] = [];
      groups[s.program_name].push(s);
    });
    return groups;
  }, [filteredStudents]);

  const handleSelectAll = () =>
    setSelectedStudentIds((prev) =>
      Array.from(new Set([...prev, ...filteredStudents.map((s) => s.student_id)])),
    );
  const handleClear = () => setSelectedStudentIds([]);
  const handleToggleStudent = (studentId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((x) => x !== studentId)
        : [...prev, studentId],
    );
  };

  const payload = {
    student_ids: pendingAudienceType === "selected" ? selectedStudentIds : [],
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const response = await updateSurvey(survey.id, payload);
      if (response.status === 200) {
        enqueueSnackbar(response.data.message.success, { variant: "success" });
        if (onAudienceSaved) onAudienceSaved();
      } else {
        enqueueSnackbar(response.data.message.error, { variant: "error" });
      }
    } catch (error) {
      console.error("API Error:", error.response);
      const errors = error.response?.data?.message?.errors;
      enqueueSnackbar(
        errors?.invalid_student_ids?.[0] || "Could not update the audience",
        { variant: "error" },
      );
    } finally {
      setIsSaving(false);
    }
  };

  const modeLabel = (type) =>
    type === "selected" ? "Selected students" : "Whole class";

  if (isLoading) {
    return (
      <div className="card survey-panel">
        <div className="survey-panel-body">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card survey-panel">
        <div className="survey-panel-title">Who receives this survey</div>
        <div className="survey-panel-body">
          <p className="survey-panel-hint">
            Class targeting stays as it is — this only decides whether every
            student of{" "}
            {(survey.programs || [])
              .map((p) => p.name ?? p.program?.name)
              .join(" & ")}{" "}
            gets it, or just the ones you name.
          </p>

          <div className="survey-option-cards">
            <div
              className={`survey-option-card ${pendingAudienceType === "whole" ? "selected" : ""}`}
              onClick={() => setPendingAudienceType("whole")}
            >
              <input
                type="radio"
                readOnly
                checked={pendingAudienceType === "whole"}
              />
              <div>
                <div className="survey-option-card-title">Whole class</div>
                <div className="survey-option-card-desc">
                  All non-suspended students of the targeted class(es).
                </div>
              </div>
            </div>
            <div
              className={`survey-option-card ${pendingAudienceType === "selected" ? "selected" : ""}`}
              onClick={() => setPendingAudienceType("selected")}
            >
              <input
                type="radio"
                readOnly
                checked={pendingAudienceType === "selected"}
              />
              <div>
                <div className="survey-option-card-title">
                  Selected students
                </div>
                <div className="survey-option-card-desc">
                  Only the students you tick. Everyone else never sees it.
                </div>
              </div>
            </div>
          </div>

          {pendingAudienceType === "selected" && (
            <>
              <div className="survey-audience-toolbar">
                <div className="survey-search-box">
                  <FaSearch className="survey-search-icon" />
                  <Input
                    type="text"
                    name="student-search"
                    placeholder="Search name / Admission Number..."
                    value={search}
                    onChangeHandler={(e) => setSearch(e.target.value)}
                  />
                </div>
                <Button variant="light" onClick={handleSelectAll}>
                  Select all
                </Button>
                <Button variant="light" onClick={handleClear}>
                  Clear
                </Button>
                <span className="survey-audience-toolbar-count">
                  {selectedStudentIds.length} of {totalAssigned} selected
                </span>
              </div>

              <div className="survey-student-list">
                {Object.entries(groupedByClass).map(([programName, rows]) => (
                  <div key={programName}>
                    <div className="survey-student-group-header">
                      {(programName || "").toUpperCase()}
                    </div>
                    {rows.map((s) => (
                      <label key={s.student_id} className="survey-student-row">
                        <span className="survey-avatar">
                          {initials(s.name)}
                        </span>
                        <span className="survey-student-row-info">
                          <span className="survey-student-row-name">
                            {s.name}
                          </span>
                          <span className="survey-student-row-meta">
                            Roll {s.roll_no} · {s.id_number}
                          </span>
                        </span>
                        <span className="survey-meta-chip survey-chip-solid">
                          {s.program_name}
                        </span>
                        <input
                          type="checkbox"
                          checked={selectedStudentIds.includes(s.student_id)}
                          onChange={() => handleToggleStudent(s.student_id)}
                        />
                      </label>
                    ))}
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination
                  totalPage={totalPages}
                  currentPage={currentPage - 1}
                  handlePageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* <div className="card survey-panel">
        <div className="survey-panel-title">Pending change</div>
        <div className="survey-panel-body">
          <div className="survey-detail-row">
            <div className="survey-detail-row-label">Now</div>
            <div className="survey-detail-row-value">
              <span className="survey-mode-chip">
                <span className="survey-mode-chip-dot"></span>
                <FaSchool /> {modeLabel(survey.is_student_specific ? 'selected' : 'whole')}
              </span>
            </div>
          </div>
          <div className="survey-detail-row">
            <div className="survey-detail-row-label">After save</div>
            <div className="survey-detail-row-value">
              <span className="survey-mode-chip">
                <span className="survey-mode-chip-dot"></span>
                <FaSchool /> {modeLabel(pendingAudienceType)}
              </span>
            </div>
          </div>

          <Button variant="primary" className="survey-save-btn" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save audience'}
          </Button>
          <div className="survey-save-hint">
            Students must be enrolled in a targeted class — the API returns 422 with{' '}
            <code>invalid_student_ids</code> otherwise.
          </div>
        </div>
      </div> */}
    </>
  );
};

export default SurveyAudienceTab;
